/**
 * Value renderers: RelativeTime, ClockTime, Elapsed, Money, PhoneNumber, Num, Duration,
 * RecordId, ShortId, Count.
 *
 * These exist so that a timestamp is never rendered as a raw string, money is never
 * rendered without money() (doctrine 3), and a phone number always carries both its
 * readable form and its dialable one. Screens compose these instead of formatting.
 *
 * Everything here carries the `nd-num` class: mono, tabular. That is not typography for its
 * own sake — a column of misaligned digits is a column an operator has to read one row at a
 * time, and the approved sheet uses mono for exactly this set: times, phone numbers,
 * durations, record ids, money.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import {
  absoluteTime,
  clockTime,
  duration as fmtDuration,
  durationWords,
  formatPhone,
  money as fmtMoney,
  num as fmtNum,
  relativeTickMs,
  relativeTime,
  shortId as fmtShortId,
  telHref,
} from '../lib/format.ts';
import { useTenantZone } from '../lib/useApi.ts';

/**
 * Relative on the surface, absolute on hover — both, always. `live` re-renders the label
 * on a schedule that matches its own age (every 15s while it is minutes old, every 10
 * minutes once it is days old), so a Tonight page left open does not quietly go stale.
 */
export function RelativeTime({
  at,
  live = true,
  prefix,
  className,
}: {
  at: string | null | undefined;
  live?: boolean;
  prefix?: string;
  className?: string;
}): ReactElement {
  const tz = useTenantZone();
  const [, tick] = useState(0);

  useEffect(() => {
    if (!live || !at) return;
    const handle = window.setInterval(() => tick((n) => n + 1), relativeTickMs(at));
    return () => window.clearInterval(handle);
  }, [at, live]);

  if (!at) return <span className={['nd-dim', className].filter(Boolean).join(' ')}>—</span>;

  return (
    <time className={className} dateTime={at} title={absoluteTime(at, tz)}>
      {prefix ? `${prefix} ` : ''}
      {relativeTime(at)}
    </time>
  );
}

/** A wall-clock time in the tenant's zone, with the full timestamp on hover. */
export function ClockTime({ at, className }: { at: string | null | undefined; className?: string }): ReactElement {
  const tz = useTenantZone();
  if (!at) return <span className="nd-dim nd-num">—</span>;
  return (
    <time className={['nd-num', className].filter(Boolean).join(' ')} dateTime={at} title={absoluteTime(at, tz)}>
      {clockTime(at, tz)}
    </time>
  );
}

/**
 * How long something has been open — "5h 56m", ticking. This is the number an operator
 * actually reads on an incident header, and it has to keep moving on a page nobody is
 * touching, because a stale elapsed time is worse than none.
 */
export function Elapsed({
  since,
  className,
  suffix,
}: {
  since: string | null | undefined;
  className?: string;
  /** "open", "into the window". Appended after the duration. */
  suffix?: string;
}): ReactElement {
  const tz = useTenantZone();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!since) return;
    const handle = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(handle);
  }, [since]);

  if (!since) return <span className="nd-dim nd-num">—</span>;
  const started = new Date(since).getTime();
  if (Number.isNaN(started)) return <span className="nd-dim nd-num">—</span>;

  return (
    <span className={['nd-num', className].filter(Boolean).join(' ')} title={`Started ${absoluteTime(since, tz)}`}>
      {durationWords(Math.max(0, (now - started) / 1000))}
      {suffix ? ` ${suffix}` : ''}
    </span>
  );
}

/**
 * Integer cents in, "$1,204.50" out, via the server's money(). There is no `dollars` prop
 * and no formatting option: one renderer, one output, everywhere (doctrine 3).
 */
export function Money({
  cents,
  className,
  tone,
  title,
}: {
  cents: number | null | undefined;
  className?: string;
  /** 'muted' for a projected/estimated figure, 'danger' for a balance owed. */
  tone?: 'default' | 'muted' | 'ok' | 'danger';
  title?: string;
}): ReactElement {
  if (cents === null || cents === undefined) {
    return <span className="nd-dim nd-num">—</span>;
  }
  const color =
    tone === 'muted'
      ? 'var(--nd-ink-2)'
      : tone === 'ok'
        ? 'var(--nd-succ)'
        : tone === 'danger'
          ? 'var(--nd-crit)'
          : undefined;
  return (
    <span
      className={['nd-num', className].filter(Boolean).join(' ')}
      style={color ? { color } : undefined}
      title={title}
    >
      {fmtMoney(cents)}
    </span>
  );
}

/**
 * (304) 555-0147, and a tel: link when the operator is on a device that can dial.
 *
 * NOTE, doctrine 1: this renders a number an operator may call back at their own
 * discretion. It is not a bridge, and there is no control anywhere in this console that
 * connects a live caller to a technician. The caller always leaves a message first.
 */
export function PhoneNumber({
  e164,
  link = true,
  className,
}: {
  e164: string | null | undefined;
  link?: boolean;
  className?: string;
}): ReactElement {
  if (!e164) return <span className="nd-dim nd-num">—</span>;
  const pretty = formatPhone(e164);
  const href = telHref(e164);
  const classes = ['nd-num', className].filter(Boolean).join(' ');

  if (!link || !href) {
    return (
      <span className={classes} title={e164}>
        {pretty}
      </span>
    );
  }
  return (
    <a className={classes} href={href} title={`Call back ${pretty}`} style={{ color: 'inherit' }}>
      {pretty}
    </a>
  );
}

/** A plain integer with thousands separators, tabular. */
export function Num({
  value,
  className,
  title,
}: {
  value: number | null | undefined;
  className?: string;
  title?: string;
}): ReactElement {
  return (
    <span className={['nd-num', className].filter(Boolean).join(' ')} title={title}>
      {fmtNum(value)}
    </span>
  );
}

/** mm:ss / h:mm:ss — call lengths and recording positions. */
export function Duration({
  seconds,
  className,
  words = false,
}: {
  seconds: number | null | undefined;
  className?: string;
  /** "1h 12m" instead of "1:12:00", for prose and badges. */
  words?: boolean;
}): ReactElement {
  return (
    <span className={['nd-num', className].filter(Boolean).join(' ')}>
      {words ? durationWords(seconds) : fmtDuration(seconds)}
    </span>
  );
}

/**
 * A human-facing record id — INC-4471, ESC-2291, #7241. Mono, and click to copy, because
 * an operator on the phone to a crew reads this out loud.
 */
export function RecordId({
  id,
  className,
  label,
}: {
  id: string | null | undefined;
  className?: string;
  /** What kind of id it is, for the screen-reader label. */
  label?: string;
}): ReactElement {
  const [copied, setCopied] = useState(false);
  if (!id) return <span className="nd-dim nd-num">—</span>;
  return (
    <button
      type="button"
      className={['nd-num', className].filter(Boolean).join(' ')}
      title={copied ? 'Copied' : `${id} — click to copy`}
      aria-label={`Copy ${label ?? 'id'} ${id}`}
      onClick={() => {
        void navigator.clipboard?.writeText(id).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1400);
        });
      }}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: copied ? 'var(--nd-succ)' : 'var(--nd-ink-2)',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      {id}
    </button>
  );
}

/** First eight of a uuid, whole id on hover, click to copy. */
export function ShortId({ id }: { id: string | null | undefined }): ReactElement {
  const [copied, setCopied] = useState(false);
  if (!id) return <span className="nd-dim nd-num">—</span>;
  return (
    <button
      type="button"
      className="nd-num"
      title={copied ? 'Copied' : `${id} — click to copy`}
      aria-label={`Copy id ${id}`}
      onClick={() => {
        void navigator.clipboard?.writeText(id).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1400);
        });
      }}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: copied ? 'var(--nd-succ)' : 'var(--nd-ink-2)',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      {fmtShortId(id)}
    </button>
  );
}

/**
 * "12 calls" / "1 call". Every list in the console shows its count — a table without one
 * makes an operator scroll to find out whether they are looking at everything.
 */
export function Count({ value, one, many }: { value: number; one: string; many?: string }): ReactElement {
  return (
    <span>
      <span className="nd-count">{fmtNum(value)}</span> {value === 1 ? one : (many ?? `${one}s`)}
    </span>
  );
}
